/**
 * Lists card container ("تخصيص القوائم والترتيب", original/src/App.tsx:13077-13463): the three order lists
 * bound to the viewer's `ui.sidebar_order` / `ui.settings_order` / `ui.dashboard_order` preferences
 * (`PUT /preferences`, D-123). Items shown = every section / card / dashboard card the viewer is permitted
 * to see, each with its label (D-124: "every label and only permitted items, including dynamic items" —
 * the old list left `installments` and `customer_portal` unlabeled and listed the two custody cards even
 * when custody was off, App.tsx:13114-13138, 13381-13409). A move swaps the two keys in the *full* order
 * (App.tsx:13176-13235) so hidden items keep their place. The icon-edit button opens the icon picker in B4.
 */
import { MenuCustomizationCard, type OrderList } from "@faylasuf/ui";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import type { UiPrefs } from "../../app/preferences.js";
import { SECTIONS, fullSectionOrder, orderedSections, swapInOrder, type SectionFlags } from "../../app/sections.js";
import { useToast } from "../../app/toast.js";
import { DASHBOARD_CARD_LABEL_KEY, dashboardCardAllowed, fullDashboardOrder } from "../DashboardPage.js";
import { SETTINGS_CARD_LABEL_KEY, fullSettingsOrder, settingsCardAllowed } from "../SettingsPage.js";

export interface MenuCustomizationContainerProps {
  readonly prefs: UiPrefs;
  readonly setPreference: (key: string, value: unknown, onError?: (e: unknown) => void) => void;
  readonly flags: SectionFlags;
  readonly onComingB4: () => void;
}

export function MenuCustomizationContainer({ prefs, setPreference, flags, onComingB4 }: MenuCustomizationContainerProps) {
  const { t } = useApp();
  const { hasPermission, sessionLost } = useSession();
  const toast = useToast();
  const onError = (e: unknown) => (isSessionLost(e) ? sessionLost() : toast(errorMessage(t, e), "error"));

  const list = (prefKey: string, full: readonly string[], visible: readonly string[], labelKey: (k: string) => string): OrderList => {
    const items = visible.map((key) => ({ key, label: t(labelKey(key)) }));
    const swap = (i: number, j: number) => {
      const a = visible[i];
      const b = visible[j];
      if (a === undefined || b === undefined) return;
      setPreference(prefKey, swapInOrder(full, a, b), onError);
    };
    return { items, onMoveUp: (i) => swap(i, i - 1), onMoveDown: (i) => swap(i, i + 1) };
  };

  const sidebarFull = fullSectionOrder(prefs.sidebarOrder, flags);
  const settingsFull = fullSettingsOrder(prefs.settingsOrder);
  const dashboardFull = fullDashboardOrder(prefs.dashboardOrder);

  return (
    <MenuCustomizationCard
      {...(prefs.icons["menu_customization"] ? { customIcon: prefs.icons["menu_customization"]! } : {})}
      sidebar={list("ui.sidebar_order", sidebarFull, orderedSections(prefs.sidebarOrder, hasPermission, flags), (k) => SECTIONS[k as keyof typeof SECTIONS].labelKey)}
      onEditIcon={onComingB4}
      settings={list("ui.settings_order", settingsFull, settingsFull.filter((k) => settingsCardAllowed(k, hasPermission)), (k) => SETTINGS_CARD_LABEL_KEY[k as keyof typeof SETTINGS_CARD_LABEL_KEY])}
      dashboard={list("ui.dashboard_order", dashboardFull, dashboardFull.filter((k) => dashboardCardAllowed(k, hasPermission)), (k) => DASHBOARD_CARD_LABEL_KEY[k as keyof typeof DASHBOARD_CARD_LABEL_KEY])}
    />
  );
}
